import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Alert, Button, Card, Field, Input, Select } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import type { Org } from '../../lib/types';

export function NewOrgPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [f, setF] = useState({
    name: '',
    slug: '',
    adminEmail: '',
    contractRef: '',
    contractEndsAt: '',
    notes: '',
    locale: 'en',
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const org = await api<Org>('/api/v1/superadmin/orgs', {
        method: 'POST',
        json: {
          name: f.name,
          slug: f.slug || undefined,
          adminEmail: f.adminEmail,
          contractRef: f.contractRef,
          contractEndsAt: f.contractEndsAt || null,
          notes: f.notes || null,
          locale: f.locale,
        },
      });
      nav(`/superadmin/orgs/${org.id}`);
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? t([`sa.err_${e.code}`, 'sa.err_generic'], { code: e.code }) +
              (e.details ? ` ${JSON.stringify(e.details)}` : '')
          : String(e),
      );
    } finally {
      setBusy(false);
    }
  };
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF({ ...f, [k]: e.target.value });
  return (
    <div className="mx-auto max-w-xl space-y-4">
      <h1 className="text-2xl font-semibold">{t('sa.newOrg')}</h1>
      <Card>
        <div className="space-y-3">
          {err && <Alert>{err}</Alert>}
          <Field label={t('sa.name')}>
            <Input value={f.name} onChange={set('name')} placeholder="Elina Pharma" />
          </Field>
          <Field
            label={t('sa.slug')}
            hint="Lowercase a-z, 0-9 and '-' only (3-24 chars); '_' and spaces become '-'. Becomes the prefix of every product ID (e.g. nurie-ai-test-20260918-0001). Empty = derived from the name. Locked once the first product exists."
          >
            <Input value={f.slug} onChange={set('slug')} placeholder="elina-pharma" />
          </Field>
          <Field
            label={t('sa.adminEmail')}
            hint="Company address or Gmail - the person who will manage the organization."
          >
            <Input
              type="email"
              value={f.adminEmail}
              onChange={set('adminEmail')}
              placeholder="someone@company.com"
            />
          </Field>
          <Field label={t('sa.contractRef')}>
            <Input value={f.contractRef} onChange={set('contractRef')} placeholder="C-2026-001" />
          </Field>
          <Field
            label={t('sa.contractEndsAt')}
            hint="Sandbox subscription length is derived from this date (max 365 days)."
          >
            <Input type="date" value={f.contractEndsAt} onChange={set('contractEndsAt')} />
          </Field>
          <Field label={t('sa.locale')}>
            <Select value={f.locale} onChange={set('locale')}>
              <option value="en">English</option>
              <option value="ko">한국어</option>
            </Select>
          </Field>
          <Field label={t('sa.notes')}>
            <Input value={f.notes} onChange={set('notes')} />
          </Field>
          <Button onClick={() => void submit()} disabled={busy || !f.name || !f.adminEmail || !f.contractRef}>
            {t('sa.create')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
